import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import QualAPI from 'utils/QualAPI';
import { normalizeId } from '../utils/idNormalization';



// Define query keys for projects
export const projectKeys = {
  all: ['projects'],
  details: () => [...projectKeys.all, 'detail'],
  detail: (id) => [...projectKeys.details(), normalizeId(id)],
};

/**
 * Hook to fetch project data by ID
 * @param {string|number} projectId - The ID of the project to fetch
 * @returns {Object} The query result containing project data, loading state, and error
 */
/* Example Payload:
{
    "id": 16,
    "name": "",
    "description": null,
    "updated_at": "2025-08-22T21:08:20.842Z",
    "status": "unarchived",
    "transcripts": [],
    "owner": {
        "id": 10,
        "name": "Alex Limpaecher",
        "email": "alimpaecher@gmail.com"
    },
    "role": "edit",
    "is_active": true,
    "recent_transcript_id": null
}
*/
export function useProject(projectId) {
  const normalizedProjectId = normalizeId(projectId);

  return useQuery({
    queryKey: projectKeys.detail(normalizedProjectId),
    queryFn: () => QualAPI.getProject(projectId).then(response => response.data),
    enabled: !!projectId,
    retry: (failureCount, error) => {
      // Don't retry on 404s
      if (error?.name === 'HTTP404Error') return false;
      // Otherwise use default retry logic (3 retries)
      return failureCount < 3;
    },
  });
}

/**
 * Base hook to fetch all projects
 * @returns {Object} The query result containing all projects data
 */
export function useProjects() {
  return useQuery({
    queryKey: projectKeys.all,
    queryFn: () => QualAPI.getProjects().then(response => response.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch filtered projects
 * @param {boolean} isArchived - Whether to fetch archived or active projects
 * @returns {Object} The query result containing filtered projects
 */
export function useFilteredProjects(isArchived = false) {
  const { data, ...queryResult } = useProjects();
  
  const filteredProjects = useMemo(() => {
    if (!data?.projects) return [];
    
    return data.projects
      .filter(project => isArchived 
        ? (project.archived || project.status === 'archived')
        : (!project.archived && project.status !== 'archived')
      )
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [data, isArchived]);
  
  return { data: filteredProjects, ...queryResult };
}